import { useState } from "react";
import axios from "axios";
import { Check, MessageCircle, ArrowUpRight, X, Crown, Users, UserRound, PartyPopper, XCircle, Shield, Clock, CalendarClock, Percent, HeadphonesIcon, ThumbsUp, Wallet, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { BRAND, waLink, WHATSAPP_LINK } from "../brand";
import { Seo } from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  {
    id: "smart",
    name: "Smart Plan",
    tag: "Best for Regular Users",
    icon: UserRound,
    price: "₹5,999",
    period: "/ month",
    features: [
      "1 Hour Daily (Up to 3 people)",
      "Same Trained Cook",
      "Up to 4 Service Hold Days",
      "Priority Booking",
      "Flexible Rescheduling",
      "Dedicated Customer Support",
    ],
    accent: "black",
  },
  {
    id: "duo",
    name: "Duo Plan",
    tag: "Best for Small Families",
    icon: Users,
    price: "₹11,999",
    period: "/ month",
    features: [
      "1 Hour Morning + 1 Hour Evening",
      "Up to 3 people",
      "Same Trained Cook",
      "Up to 5 Service Hold Days",
      "Priority Booking",
      "Flexible Rescheduling",
      "Faster Replacement Support",
    ],
    accent: "orange",
    badge: "Most loved",
  },
  {
    id: "signature",
    name: "Signature Plan",
    tag: "Best for Medium Families",
    icon: Crown,
    price: "₹17,999",
    period: "/ month",
    features: [
      "2 Hours Morning + 2 Hours Evening",
      "Up to 6 people",
      "Same Trained Cook",
      "Up to 6 Service Hold Days",
      "Highest Booking Priority",
      "Dedicated Customer Support",
      "Flexible Rescheduling",
    ],
    accent: "purple",
  },
];

const BENEFITS = [
  { icon: Percent, t: "Save up to 30%" },
  { icon: UserRound, t: "Same Trained Cook" },
  { icon: Clock, t: "Priority Booking" },
  { icon: CalendarClock, t: "Flexible Rescheduling" },
  { icon: HeadphonesIcon, t: "Dedicated Support" },
  { icon: Shield, t: "Background Checked" },
  { icon: ThumbsUp, t: "Reliable Daily Service" },
  { icon: Wallet, t: "No Hidden Charges" },
];

const FAQS = [
  { q: "How do I start a subscription?", a: "Pick a plan, tell us your address and preferred timings on WhatsApp, and we begin within 48 hours." },
  { q: "Can I pause or reschedule?", a: "Yes — every plan includes flexible rescheduling and service hold days (4 to 6 depending on plan). No lock-ins." },
  { q: "Do you bring the groceries?", a: "No. We cook using your ingredients, your oil, your masalas — so every dish tastes like home." },
  { q: "Do subscription plans cover cleaning?", a: "No. We do not provide kitchen cleaning, utensil cleaning or serving. Cooks focus purely on cooking your meal." },
  { q: "Which areas of Lucknow do you serve?", a: `We currently serve ${BRAND.zones.join(", ")}. Message us on WhatsApp to join the waitlist for nearby areas.` },
  { q: "Same cook every day?", a: "Yes — every subscription plan includes a dedicated, same trained cook so your family adjusts to their pace and taste." },
];

const Plans = () => {
  const [openPlan, setOpenPlan] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleOpen = (plan) => {
    setForm({ name: "", phone: "", email: "", address: "", notes: "" });
    setOpenPlan(plan);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!openPlan) return;
    if (!form.name || !form.phone) {
      toast.error("Name and phone are required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/subscriptions`, { ...form, plan: openPlan.name });
      toast.success("Subscription request received", { description: "We'll WhatsApp you within the day to confirm." });
      setOpenPlan(null);
    } catch (err) {
      toast.error("Something went wrong", { description: err?.response?.data?.detail || "Please try WhatsApp instead." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="plans-page" className="bg-[#f7f5f2]">
      <Seo
        title="Smart Subscriptions — Daily Cook Plans in Lucknow | ooloo"
        description="Smart, Duo and Signature plans from ₹5,999/month. Same trained cook every day, priority booking, flexible rescheduling. Save up to 30% on daily kitchen help in Lucknow."
        path="/plans"
      />

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-12">
        <p className="eyebrow rise">Smart Subscriptions</p>
        <h1 className="font-display mt-5 text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] max-w-4xl rise rise-1">
          Same cook.
          <br />
          <span className="italic font-serif font-light text-[#c45b3e]">Every day</span>.
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[#5c5a56] leading-relaxed font-medium rise rise-2">
          Save up to 30% with a monthly plan. Same trained cook, priority booking, flexible rescheduling — designed for busy Lucknow homes.
        </p>
      </section>

      {/* PLAN CARDS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-16">
        <div className="grid md:grid-cols-3 gap-5">
          {PLANS.map((p) => {
            const Icon = p.icon;
            const isDuo = p.accent === "orange";
            const isSig = p.accent === "purple";
            return (
              <div key={p.id} data-testid={`plan-card-${p.id}`} className={`relative rounded-3xl p-8 md:p-10 flex flex-col ${isDuo ? "bg-[#c45b3e] text-white ring-4 ring-[#c45b3e]/20 md:-translate-y-3" : "bg-white border border-[#e5e2dc]"}`}>
                {p.badge && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#0a0a0a] text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full">
                    {p.badge}
                  </span>
                )}
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isDuo ? "bg-white/20 text-white" : isSig ? "bg-[#efebe6] text-[#7b3f8f]" : "bg-[#0a0a0a] text-white"}`}>
                  <Icon size={22} strokeWidth={1.8} />
                </div>
                <h2 className={`mt-5 font-display text-2xl md:text-3xl font-extrabold tracking-tight ${isDuo ? "text-white" : "text-[#0a0a0a]"}`}>{p.name}</h2>
                <p className={`text-sm ${isDuo ? "text-white/80" : "text-[#5c5a56]"}`}>{p.tag}</p>
                <div className={`mt-6 inline-flex items-baseline gap-1 rounded-2xl px-4 py-2 self-start ${isDuo ? "bg-white text-[#c45b3e]" : isSig ? "bg-[#7b3f8f] text-white" : "bg-[#0a0a0a] text-white"}`}>
                  <p className="font-display text-3xl md:text-4xl font-black tracking-tighter">{p.price}</p>
                  <p className="text-sm font-bold">{p.period}</p>
                </div>
                <ul className={`mt-6 space-y-3 flex-1 ${isDuo ? "text-white" : "text-[#0a0a0a]"}`}>
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check size={16} className={`mt-0.5 flex-shrink-0 ${isDuo ? "text-white" : "text-[#c45b3e]"}`} strokeWidth={2.5} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button data-testid={`plan-subscribe-btn-${p.id}`} onClick={() => handleOpen(p)} className={`mt-8 w-full rounded-full font-bold py-6 ${isDuo ? "bg-white text-[#c45b3e] hover:bg-[#0a0a0a] hover:text-white" : "bg-[#0a0a0a] text-white hover:bg-[#c45b3e]"}`}>
                  Choose {p.name} <ArrowUpRight size={18} className="ml-1" />
                </Button>
              </div>
            );
          })}
        </div>
      </section>

      {/* PARTY REMINDER */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="dark-card p-8 md:p-10 grid lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-8 flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-[#c45b3e] flex-shrink-0">
              <PartyPopper size={22} strokeWidth={1.8} />
            </div>
            <div>
              <p className="eyebrow text-white/60">Not a subscription — one-off</p>
              <p className="mt-2 font-display text-2xl md:text-3xl font-extrabold tracking-tight text-white">Family Gathering & Party</p>
              <p className="mt-2 text-white/70 text-sm">Up to 20 people · 2 trained cooks · Minimum 3 hours booking</p>
            </div>
          </div>
          <div className="lg:col-span-4 text-right">
            <p className="font-display text-5xl font-black tracking-tighter text-[#c45b3e]">₹999<span className="text-lg text-white/60">/hr</span></p>
            <a href={waLink("Hi ooloo, I want to book cooks for a party.")} target="_blank" rel="noreferrer" data-testid="plans-party-cta" className="inline-flex items-center gap-2 mt-3 bg-white text-[#0a0a0a] px-5 py-3 rounded-full font-bold text-sm">
              <MessageCircle size={16} strokeWidth={2.5} /> Book party
            </a>
          </div>
        </div>
      </section>

      {/* BENEFITS GRID */}
      <section className="bg-[#efebe6] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="eyebrow">Subscription benefits</p>
            <h2 className="font-display mt-4 text-4xl md:text-6xl font-black tracking-tighter leading-[0.95] text-[#0a0a0a]">
              Save more. Cook <span className="italic font-serif font-light text-[#c45b3e]">less</span>.
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {BENEFITS.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.t} data-testid={`benefit-${b.t.toLowerCase().split(" ")[0]}`} className="bg-white rounded-2xl p-5 border border-[#e5e2dc] flex flex-col items-center text-center">
                  <div className="w-11 h-11 rounded-full bg-[#efebe6] flex items-center justify-center text-[#c45b3e]">
                    <Icon size={18} strokeWidth={1.8} />
                  </div>
                  <p className="mt-3 font-display text-sm font-extrabold tracking-tight text-[#0a0a0a] leading-snug">{b.t}</p>
                </div>
              );
            })}
          </div>
          {/* Exclusions */}
          <div className="mt-8 flex flex-wrap justify-center gap-2">
            {BRAND.exclusions.map((x) => (
              <span key={x} className="inline-flex items-center gap-1 bg-white border border-[#e5e2dc] rounded-full px-3 py-1.5 text-xs font-bold text-[#0a0a0a]">
                <XCircle size={12} className="text-[#c45b3e]" /> {x}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="eyebrow">FAQ</p>
            <h2 className="font-display mt-4 text-4xl md:text-5xl font-black tracking-tighter leading-[0.95] text-[#0a0a0a]">
              Things families ask.
            </h2>
          </div>
          <div className="lg:col-span-8 divide-y divide-[#e5e2dc] border-y border-[#e5e2dc]">
            {FAQS.map((f, i) => (
              <details key={i} className="group py-5" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer flex justify-between items-center list-none">
                  <span className="font-display font-extrabold text-lg md:text-xl tracking-tight text-[#0a0a0a] pr-4">{f.q}</span>
                  <span className="w-8 h-8 rounded-full border border-[#e5e2dc] flex items-center justify-center text-[#c45b3e] group-open:rotate-45 transition-transform flex-shrink-0">
                    <ArrowUpRight size={16} />
                  </span>
                </summary>
                <p className="mt-3 text-[#5c5a56] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0a0a0a] text-white py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter">
            Start with <span className="italic font-serif font-light text-[#c45b3e]">Duo</span>.
          </h2>
          <a href={waLink("Hi ooloo, I'd like to start a Duo plan.")} target="_blank" rel="noreferrer" data-testid="plans-final-cta" className="btn-whatsapp">
            <MessageCircle size={18} strokeWidth={2.5} /> Talk on WhatsApp
          </a>
        </div>
      </section>

      {/* SUBSCRIBE DIALOG */}
      <Dialog open={!!openPlan} onOpenChange={(o) => !o && setOpenPlan(null)}>
        <DialogContent data-testid="subscribe-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-extrabold tracking-tight text-[#0a0a0a]">
              {openPlan?.name}
            </DialogTitle>
            <DialogDescription className="text-[#5c5a56]">
              Drop your details. We&apos;ll WhatsApp you within the day to confirm.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="sub-name">Full name*</Label>
              <Input id="sub-name" data-testid="sub-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="sub-phone">Phone*</Label>
                <Input id="sub-phone" data-testid="sub-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
              </div>
              <div>
                <Label htmlFor="sub-email">Email</Label>
                <Input id="sub-email" data-testid="sub-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
            </div>
            <div>
              <Label htmlFor="sub-address">Address</Label>
              <Input id="sub-address" data-testid="sub-address-input" placeholder="Area / locality, Lucknow" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="sub-notes">Notes</Label>
              <Textarea id="sub-notes" data-testid="sub-notes-input" placeholder="Preferred timings, dietary preferences…" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
            </div>
            <DialogFooter className="gap-2 flex-row">
              <DialogClose asChild>
                <Button type="button" variant="ghost" data-testid="sub-cancel-btn"><X size={16} /> Cancel</Button>
              </DialogClose>
              <Button type="submit" data-testid="sub-submit-btn" disabled={submitting} className="bg-[#0a0a0a] text-white hover:bg-[#c45b3e] rounded-full font-bold">
                {submitting ? "Sending…" : "Start subscription"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </main>
  );
};

export default Plans;
